// Sub-usuários (pedido 2026-09-10/11) — o TITULAR de uma conta Cliente
// cria/edita/apaga, sozinho, usuários adicionais pra parente/terceiro
// acessar o sistema (popup "Criar usuário" no Perfil do PWA), com um
// subconjunto restrito de veículos (só os que já são dele) e permissões
// (só o catálogo restrito, ver SUBUSER_PERMISSION_WHITELIST no backend —
// nunca administração/financeiro/gestão de usuários). Um Administrador da
// empresa também gerencia os mesmos sub-usuários pela aba USUÁRIOS em
// Editar Usuário (web) — eles nunca aparecem na lista PRINCIPAL de
// Usuários (ver companyTraccarIds, usado pra filtrar).
import { gpsDriveDelete, gpsDriveGet, gpsDrivePatch, gpsDrivePost } from "@/lib/api/gpsdrive-client";

// Snapshot local pra companyTraccarIds() (mesmo padrão/motivo do
// gpsdrive_users_list_snapshot_cache_2026-09-04 em users.service.ts):
// usersData (lista principal do Traccar) já nasce instantânea via
// initialData de cache local, mas essa lista de IDs (usada só pra FILTRAR
// sub-usuários fora da tela principal) sempre partia de undefined — cada
// F5/entrada na tela mostrava a lista completa (sub-usuários incluídos) por
// 1-2s até essa query real resolver. Bug relatado 2026-09-10: "aparece um
// monte de usuario que foi criado pelo cliente etc, e depois em segundos
// aparece so os usuarios que deveram aparecer". Cacheando o mesmo jeito
// fecha o vazio: filtro correto já no primeiro paint.
const COMPANY_SUB_USER_IDS_CACHE_KEY = "gpsdrive.companySubUserTraccarIds.cache.v1";

export function readCompanySubUserTraccarIdsCache(): number[] | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(COMPANY_SUB_USER_IDS_CACHE_KEY);
    return raw ? (JSON.parse(raw) as number[]) : undefined;
  } catch {
    return undefined;
  }
}

function writeCompanySubUserTraccarIdsCache(ids: number[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(COMPANY_SUB_USER_IDS_CACHE_KEY, JSON.stringify(ids));
  } catch {
    // localStorage indisponível (aba anônima/quota) — cache é só otimização.
  }
}

export function clearCompanySubUserTraccarIdsCache(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(COMPANY_SUB_USER_IDS_CACHE_KEY);
  } catch {
    /* noop */
  }
}

export interface SubUserPermissionInput {
  category: string;
  actions: string[];
}

export interface CreateSubUserInput {
  name: string;
  email: string;
  phone: string;
  password: string;
  deviceIds: number[];
  permissions: SubUserPermissionInput[];
}

// Tudo opcional (PATCH) — só o que veio muda; password ausente/vazio nunca
// reseta a senha existente (ver comentário equivalente no backend).
export interface UpdateSubUserInput {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
  deviceIds?: number[];
  permissions?: SubUserPermissionInput[];
}

export interface CreateSubUserResult {
  id: string;
  name: string;
  email: string;
  phone?: string;
  traccarUserId: number;
  deviceIds: number[];
}

export interface SubUserSummary {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  createdAt: string;
}

// Shape mínimo do dispositivo Traccar devolvido em availableDevices —
// mesmos campos que o resto do app já lê pra extrair placa (attributes.plate).
export interface SubUserAvailableDevice {
  id: number;
  name: string;
  attributes: Record<string, unknown>;
}

export interface SubUserDetail extends SubUserSummary {
  deviceIds: number[];
  permissions: SubUserPermissionInput[];
  // Universo de veículos pra marcar/desmarcar na edição — sempre os do
  // CRIADOR ORIGINAL, nunca de quem está editando (pode ser um
  // Administrador com acesso bem mais amplo).
  availableDevices: SubUserAvailableDevice[];
}

export const gpsDriveSubUsersService = {
  create: (input: CreateSubUserInput) =>
    gpsDrivePost<CreateSubUserResult>("/api/v1/sub-users", input),
  // Sub-usuários que EU (chamador) criei — popup "Criar usuário" do Perfil.
  listMine: () => gpsDriveGet<SubUserSummary[]>("/api/v1/sub-users"),
  listByCreator: (creatorUserId: string) =>
    gpsDriveGet<SubUserSummary[]>(`/api/v1/sub-users/by-creator/${creatorUserId}`),
  // IDs (Traccar) de todo sub-usuário da empresa — usado pra esconder
  // sub-usuários da tela principal de Usuários.
  companyTraccarIds: async () => {
    const ids = await gpsDriveGet<number[]>("/api/v1/sub-users/company-traccar-ids");
    writeCompanySubUserTraccarIdsCache(ids);
    return ids;
  },
  getDetail: (id: string) => gpsDriveGet<SubUserDetail>(`/api/v1/sub-users/${id}`),
  update: (id: string, input: UpdateSubUserInput) =>
    gpsDrivePatch<SubUserSummary>(`/api/v1/sub-users/${id}`, input),
  remove: (id: string) => gpsDriveDelete<void>(`/api/v1/sub-users/${id}`),
};
